import uniqBy from "lodash/uniqBy";
import partition from "lodash/partition";
import orderBy from "lodash/orderBy";
import omit from "lodash/omit";
import { createReducer } from "@reduxjs/toolkit";

import {
  CREATE_MESSAGE_ROUTINE,
  GET_CONVERSATIONS_ROUTINE,
  GET_MESSAGES_ROUTINE,
  PATCH_CONVERSATION_ROUTINE,
  SELECT_CONVERSATION_ACCOUNTS_ROUTINE,
  UPDATE_STAR_FILTER_ROUTINE,
  UPDATE_CONVERSATION_CONFIG_ROUTINE,
  DELETE_CONVERSATION_ROUTINE,
  GET_CONVERSATIONS_COUNTER_ROUTINE,
  ADD_CONVERSATION_ROUTINE,
  REMOVE_CONVERSATION_ROUTINE,
  ADD_MESSAGE_ROUTINE,
  REMOVE_MESSAGE_ROUTINE,
  GET_CONVERSATION_ROUTINE,
  ADD_COLLABORATOR_TYPING_ROUTINE,
  REMOVE_COLLABORATOR_TYPING_ROUTINE,
  RESYNC_INTERLOCUTOR,
  ADD_MESSAGE_REACTION_ROUTINE,
  REMOVE_MESSAGE_REACTION_ROUTINE,
  CLOSE_ALL_CONVERSATIONS_ROUTINE,
  SYNC_MESSAGES_ROUTINE,
  SET_CONVERSATION_TYPES_FILTER_ROUTINE,
  SELECT_CONVERSATIONS_ROUTINE,
  PATCH_CONVERSATIONS_ROUTINE,
  SET_COLLABORATOR_TYPINGS_ROUTINE,
  INBOX_SET_MESSAGE_STATUS_REMOVED_ROUTINE,
  INBOX_SET_MESSAGE_HIDDEN_ROUTINE,
  RESYNC_POST_CONTENT,
  INBOX_SET_MESSAGE_STATUS_EDITING_ROUTINE,
  NOTIFY_INBOX_URL_CHANGE_ROUTINE,
  DELETE_MESSAGE_STATUS_ROUTINE,
} from "constants/ActionTypes";

import {
  ConversationPost,
  InboxLocalStorageKey,
} from "types/conversationInbox";

import { getLocalStorageSelectedAccounts } from "libs/storage/adapters";

import {
  Conversation,
  Conversations,
  Pagination,
  Message,
  ConversationsFilters,
  Status,
  TypingCollaborator,
  MessageStatus,
  ConversationsResponse,
  MessageType,
  ConversationType,
  Tag,
} from "types/conversationInbox";

import { immutableUpdate, immutableAdd } from "./reducerUtils";
import {
  AddReactionPayload,
  FetchMessagesAction,
  FetchMessagesSuccessAction,
  patchConversationsAction,
  RemoveReactionPayload,
  setConversationTypesFilterAction,
  selectConversationsPayload,
  setMessageReactionProcessingPayload,
  SyncMessagesAction,
  addMessageSuccessAction,
  collaboratorTypingAction,
  setMessageStatusRemovedAction,
  setMessageHiddenAction,
  setMessageStatusEditingAction,
  hydrateInboxFiltersFromUrl,
  HydrateInboxFiltersFromUrlPayload,
  setSearchTerm,
  setSentimentFilter,
  setVisibleFacets,
  setTagFilter,
  setDateRangeFilter,
  fetchWorkspaceTagsSuccess,
  fetchWorkspaceTagsFailure,
  fetchWorkspaceTags,
  createWorkspaceTagSuccess,
  createWorkspaceTagFailure,
  updateWorkspaceTagSuccess,
  updateWorkspaceTagFailure,
  deleteWorkspaceTagSuccess,
  deleteWorkspaceTagFailure,
  fetchArchivedTags,
  fetchArchivedTagsSuccess,
  fetchArchivedTagsFailure,
  restoreWorkspaceTag,
  restoreWorkspaceTagSuccess,
  restoreWorkspaceTagFailure,
  bulkAddConversationTagSuccess,
  bulkAddConversationTagFailure,
  bulkRemoveConversationTagSuccess,
  bulkRemoveConversationTagFailure,
  setMessageReactionProcessing,
} from "actions/conversationInbox";
import {
  immutableConversationUpdate,
  immutableMessageUpdate,
  immutableArrReplaceById,
  immutableUpdateById,
  immutableUpdateByIdOrAdd,
  immutableRemoveById,
} from "./immutableUtils";

type ConversationFilterConfigType = "MENTION";
export interface ConversationFilterConfig {
  isStarred: boolean;
  isMentioned: boolean;
  assignedTo: string | null;
  type: ConversationFilterConfigType | null;
  status: Status | null;
  unassigned: boolean | null;
  awaitingReply: boolean;
}

export enum ConversationSort {
  Newest = "updatedAt:DESC",
  Oldest = "updatedAt:ASC",
  "Saved first" = "isStarred:DESC",
}

export type ConversationInboxState = Conversations & {
  isConversationLoading: boolean;
  isConversationJustCleared: boolean;
  isConversationCountersLoading: boolean;
  sort: string;
  isStarred: boolean;
  isMentioned?: boolean;
  isConversationUpdating: boolean;
  selectedAccounts: string[];
  assignedTo: string | null;
  status: Status | null;
  counter: ConversationsFilters;
  typingCollaborators: TypingCollaborator[];
  unassigned: boolean | null;
  awaitingReply: boolean;
  isClosingAllConversationRequest?: boolean;
  types?: ConversationType[];
  selectedConversations: string[];
  editingMessage: Message | null;
  searchTerm: string;
  sentiment: string[];
  visibleFacets: string[];
  tagFilter: string[];
  dateFrom: string | null;
  dateTo: string | null;
  workspaceTags: Tag[];
  isWorkspaceTagsLoading: boolean;
  workspaceTagsError: string | null;
  archivedTags: Tag[];
  isArchivedTagsLoading: boolean;
  archivedTagsError: string | null;
  restoringTagIds: string[];
};

export const defaultConfigState: ConversationFilterConfig = {
  isStarred: false,
  isMentioned: false,
  assignedTo: null,
  type: null,
  status: null,
  unassigned: null,
  awaitingReply: false,
};

export const defaultFilterState: ConversationsFilters = {
  all: null,
  allCapped: null,
  unassigned: null,
  unassignedCapped: null,
  assignedToMe: null,
  assignedToMeCapped: null,
  mentioned: null,
  mentionedCapped: null,
  starred: null,
  starredCapped: null,
  closed: null,
  closedCapped: null,
  unreadConversations: null,
  unreadConversationsCapped: null,
  taggedInPost: null,
  taggedInPostCapped: null,
  awaitingReply: null,
  awaitingReplyCapped: null,
};

export const DEFAULT_INBOX_CONVERSATION_TYPES: ConversationType[] = [
  "MESSAGE",
  "THREADED_COMMENT",
  "POST",
];

export const normalizeInboxConversationTypes = (
  types: unknown
): ConversationType[] =>
  Array.isArray(types) && types.length > 0
    ? (types as ConversationType[])
    : DEFAULT_INBOX_CONVERSATION_TYPES;

export const initialState: ConversationInboxState = {
  isConversationLoading: false,
  isConversationUpdating: false,
  isConversationCountersLoading: false,
  // [24842] if the last conversation is handled, will mark as true and show a confetti
  isConversationJustCleared: false,
  conversations: [],
  typingCollaborators: [],
  next: null,
  previous: null,
  hasPrevious: false,
  hasNext: false,
  sort: ConversationSort.Newest,
  selectedAccounts: JSON.parse(
    getLocalStorageSelectedAccounts() || JSON.stringify([])
  ),
  types: normalizeInboxConversationTypes(
    JSON.parse(
      localStorage.getItem(
        InboxLocalStorageKey.inboxSelectedConversationTypes
      ) || JSON.stringify(DEFAULT_INBOX_CONVERSATION_TYPES)
    )
  ),
  selectedConversations: [],
  ...defaultConfigState,
  counter: defaultFilterState,
  editingMessage: null,
  searchTerm: "",
  sentiment: [],
  visibleFacets: ["status", "type", "assigned"],
  tagFilter: [],
  dateFrom: null,
  dateTo: null,
  workspaceTags: [],
  isWorkspaceTagsLoading: false,
  workspaceTagsError: null,
  archivedTags: [],
  isArchivedTagsLoading: false,
  archivedTagsError: null,
  restoringTagIds: [],
};

const conversationInboxReducer = createReducer(initialState, (builder) => {
  builder
    .addCase(NOTIFY_INBOX_URL_CHANGE_ROUTINE.REQUEST, (state) => {
      return {
        ...state,
        editingMessage: null,
      };
    })
    .addCase(GET_CONVERSATIONS_ROUTINE.REQUEST, (state) => {
      return {
        ...state,
        isConversationJustCleared: false,
        isConversationLoading: true,
      };
    })
    .addCase(GET_CONVERSATIONS_ROUTINE.SUCCESS, (state, action) => {
      const {
        next = null,
        previous = null,
        hasPrevious = false,
        hasNext = false,
        items = [],
      }: ConversationsResponse = action?.payload?.data ?? {};
      const reset = action?.payload?.reset;
      const existingConversations: Conversations["conversations"] = reset
        ? []
        : state.conversations || [];

      const newConversations = reset
        ? items.reduce((list, conversation) => {
            const existingConv = state.conversations?.find(
              (stateConv) => stateConv.id === conversation.id
            );
            if (existingConv) {
              return [
                ...list,
                {
                  ...conversation,
                  ...existingConv,
                },
              ];
            }
            return [...list, conversation];
          }, [] as Conversation[])
        : items;

      const uniqConverations = uniqBy(
        [...existingConversations, ...newConversations],
        "id"
      );
      return {
        ...state,
        next,
        previous,
        hasPrevious,
        hasNext,
        conversations: uniqConverations,
        isConversationLoading: false,
      };
    })
    .addCase(GET_CONVERSATIONS_ROUTINE.FAILURE, (state) => {
      return {
        ...state,
        isConversationLoading: false,
      };
    })
    .addCase(GET_CONVERSATION_ROUTINE.REQUEST, (state) => {
      return {
        ...state,
        isConversationLoading: true,
      };
    })
    .addCase(GET_CONVERSATION_ROUTINE.SUCCESS, (state, action) => {
      const conversations: Conversations["conversations"] =
        state.conversations || [];
      const conversationIdIndex: number = conversations.findIndex(
        (conversation: Conversation) =>
          conversation.id === action.payload.data.id
      );

      if (conversationIdIndex === -1) {
        const sortedConversations = orderBy(conversations, "updatedAt", "desc");
        return {
          ...state,
          conversations: [action.payload.data, ...sortedConversations],
        };
      } else {
        return {
          ...state,
          conversations: immutableUpdate(
            conversations,
            conversationIdIndex,
            action.payload.data
          ),
        };
      }
    })
    .addCase(GET_CONVERSATION_ROUTINE.FAILURE, (state) => {
      return {
        ...state,
        isConversationLoading: false,
      };
    })
    .addCase(
      GET_MESSAGES_ROUTINE.TRIGGER,
      (state, action: FetchMessagesAction): ConversationInboxState => {
        const conversationId = action.payload.conversationId;
        return {
          ...state,
          conversations: immutableUpdateById(
            state.conversations || [],
            conversationId,
            (conv) => ({
              ...conv,
              isMessagesLoading: true,
              isMessagesFetched: true,
              isLastMessagesFetchedError: false,
              lastMessagesFetchedTime: new Date(),
            })
          ),
        };
      }
    )
    .addCase(
      GET_MESSAGES_ROUTINE.SUCCESS,
      (
        state: ConversationInboxState,
        action: FetchMessagesSuccessAction
      ): ConversationInboxState => {
        const conversations = state.conversations || [];
        const conversationIdIndex = conversations.findIndex(
          (c) => c.id === action?.payload?.conversationId
        );

        if (conversationIdIndex === -1) {
          return state;
        }

        const {
          next = null,
          previous = null,
          hasPrevious = false,
          hasNext = false,
          items,
        }: Pagination<Message> = action?.payload?.data ?? {};
        const conv = conversations[conversationIdIndex];
        let existingMessages = conv?.messages ?? [];

        const [replacingMessages, addedMessages] = partition(items, (c) =>
          existingMessages.some((d) => c.id == d.id)
        );

        existingMessages = immutableArrReplaceById(
          existingMessages,
          replacingMessages
        );
        const { setMessagesOnly } = action.payload;
        const paginationInfo: Partial<Conversation> = setMessagesOnly
          ? {}
          : {
              next,
              previous,
              hasPrevious,
              hasNext,
            };
        return {
          ...state,
          conversations: immutableUpdate(conversations, conversationIdIndex, {
            ...paginationInfo,
            messages: orderMessages([...existingMessages, ...addedMessages]),
            isMessagesLoading: false,
            isMessagesFetched: true,
            isLastMessagesFetchedError: false,
            lastMessagesFetchedTime: new Date(),
          }),
        };
      }
    )
    .addCase(GET_MESSAGES_ROUTINE.FAILURE, (state, action) => {
      const conversationId = action.payload.conversationId;
      return {
        ...state,
        conversations: immutableUpdateById(
          state.conversations || [],
          conversationId,
          (conv) => ({
            ...conv,
            isMessagesLoading: false,
            isMessagesFetched: true,
            isLastMessagesFetchedError: true,
          })
        ),
      };
    })
    .addCase(
      SYNC_MESSAGES_ROUTINE.REQUEST,
      (state, action: SyncMessagesAction) => {
        return immutableConversationUpdate({
          state,
          convId: action.payload.conversationId,
          updater: (c) => ({ ...c, lastMessagesFetchedTime: new Date() }),
        });
      }
    )
    .addCase(CREATE_MESSAGE_ROUTINE.REQUEST, (state, action) => {
      const conversationId: string = action?.payload?.conversationId;
      const conversations: Conversations["conversations"] =
        state.conversations || [];
      const conversationIdIndex: number = conversations.findIndex(
        (conversation: Conversation) => conversation.id === conversationId
      );
      const existingMessages =
        conversations[conversationIdIndex]?.messages ?? [];
      state.conversations = immutableUpdate(
        conversations,
        conversationIdIndex,
        {
          messages: [...existingMessages, action.payload.message],
        }
      );
    })
    .addCase(CREATE_MESSAGE_ROUTINE.SUCCESS, (state, action) => {
      const { conversationId, message, temporaryId } = action?.payload ?? {};
      const conversations: Conversations["conversations"] =
        state.conversations || [];
      const conversationIdIndex: number = conversations.findIndex(
        (conversation: Conversation) => conversation.id === conversationId
      );

      if (conversationIdIndex === -1) {
        return;
      }

      let existingMessages = conversations[conversationIdIndex]?.messages ?? [];
      const targetMessageIndex = existingMessages.findIndex(
        (message) => message.id === temporaryId
      );

      if (targetMessageIndex !== -1) {
        existingMessages = immutableUpdate(
          existingMessages,
          targetMessageIndex,
          {
            ...message,
            status: MessageStatus.success,
          }
        );

        return {
          ...state,
          conversations: immutableUpdate(conversations, conversationIdIndex, {
            messages: [...existingMessages],
          }),
        };
      }

      return state;
    })
    .addCase(CREATE_MESSAGE_ROUTINE.FAILURE, (state, action) => {
      const conversationId: string = action?.payload?.conversationId;
      const conversations: Conversations["conversations"] =
        state.conversations || [];
      const conversationIdIndex: number = conversations.findIndex(
        (conversation: Conversation) => conversation.id === conversationId
      );

      let existingMessages = conversations[conversationIdIndex]?.messages ?? [];
      const targetMessageIndex = existingMessages.findIndex(
        (message) => message.id === action.payload.message.id
      );

      if (targetMessageIndex !== -1) {
        existingMessages = immutableUpdate(
          existingMessages,
          targetMessageIndex,
          {
            ...existingMessages[targetMessageIndex],
            status: MessageStatus.failed,
          }
        );

        return {
          ...state,
          conversations: immutableUpdate(conversations, conversationIdIndex, {
            messages: [...existingMessages],
          }),
        };
      }

      return state;
    })
    .addCase(UPDATE_STAR_FILTER_ROUTINE.REQUEST, (state, action) => {
      return {
        ...state,
        isStarred: action.payload.isStarred,
        next: null,
      };
    })
    .addCase(PATCH_CONVERSATION_ROUTINE.REQUEST, (state, action) => {
      const { conversationId, ...conversation } = action?.payload ?? {};

      const conversations: Conversations["conversations"] =
        state.conversations || [];
      const conversationIdIndex: number = conversations.findIndex(
        (conversation: Conversation) => conversation.id === conversationId
      );

      if (conversationIdIndex !== -1) {
        const targetConversation = conversations[conversationIdIndex];

        return {
          ...state,
          conversations: immutableUpdate(conversations, conversationIdIndex, {
            ...targetConversation,
            ...conversation,
          }),
          isConversationUpdating: false,
        };
      }

      return {
        ...state,
        isConversationUpdating: false,
      };
    })
    .addCase(PATCH_CONVERSATION_ROUTINE.SUCCESS, (state, action) => {
      const conversation: Conversation = action.payload.data;
      const conversationId: string = conversation.id;

      const conversations: Conversations["conversations"] =
        state.conversations || [];
      const conversationIdIndex: number = conversations.findIndex(
        (conversation: Conversation) => conversation.id === conversationId
      );

      if (conversationIdIndex !== -1) {
        const orignalConv = conversations[conversationIdIndex];

        const updatingConversation: Partial<Conversation> = {
          ...orignalConv,
          ...conversation,
          // Only merge post if both exist, otherwise use whichever is defined
          ...(conversation.post || orignalConv?.post
            ? {
                post: {
                  ...orignalConv?.post,
                  ...conversation.post,
                } as ConversationPost,
              }
            : {}),
        };

        return {
          ...state,
          conversations: immutableUpdate(
            conversations,
            conversationIdIndex,
            updatingConversation
          ),
          isConversationUpdating: false,
        };
      }

      return {
        ...state,
        isConversationUpdating: false,
      };
    })
    .addCase(PATCH_CONVERSATION_ROUTINE.FAILURE, (state, action) => {
      const conversation: Conversation = action?.payload?.conversation;
      const conversations: Conversations["conversations"] =
        state.conversations || [];
      const conversationIdIndex: number = conversations.findIndex(
        (conv: Conversation) => conv.id === conversation.id
      );

      if (conversationIdIndex !== -1) {
        return {
          ...state,
          conversations: immutableUpdate(conversations, conversationIdIndex, {
            ...conversation,
          }),
          isConversationUpdating: false,
        };
      }

      return {
        ...state,
        isConversationUpdating: false,
      };
    })
    .addCase(PATCH_CONVERSATIONS_ROUTINE.REQUEST, (state, action) => {
      const {
        conversationIds,
        ...updateConversation
      }: patchConversationsAction["payload"] = action.payload;
      const conversations: Conversations["conversations"] =
        state.conversations || [];

      return {
        ...state,
        conversations: conversations.map((conversation) => {
          if (conversationIds.includes(conversation.id)) {
            return {
              ...conversation,
              ...updateConversation,
            } as Conversation;
          }
          return conversation;
        }),
      };
    })
    .addCase(PATCH_CONVERSATIONS_ROUTINE.SUCCESS, (state, action) => {
      const { data }: { data: Conversation[] } = action.payload;
      const conversations: Conversations["conversations"] =
        state.conversations || [];

      const updatedConversationMap = data.reduce(
        (accMap, conversation) => {
          accMap[conversation.id] = conversation;
          return accMap;
        },
        {} as Record<string, Conversation>
      );

      const updatedConversationIds = Object.keys(updatedConversationMap);

      return {
        ...state,
        conversations: conversations.map((conversation) => {
          if (updatedConversationIds.includes(conversation.id)) {
            return {
              ...conversation,
              ...updatedConversationMap[conversation.id],
            };
          }
          return {
            ...conversation,
          };
        }),
      };
    })
    .addCase(PATCH_CONVERSATIONS_ROUTINE.FAILURE, (state, action) => {
      const { conversations }: { conversations: Conversation[] } =
        action.payload;
      return {
        ...state,
        conversations: [...conversations],
      };
    })
    .addCase(
      SELECT_CONVERSATION_ACCOUNTS_ROUTINE.REQUEST,
      (state, action: { payload: string[] }) => {
        const selectedAccounts = action.payload;
        return {
          ...state,
          selectedAccounts,
        };
      }
    )
    .addCase(UPDATE_CONVERSATION_CONFIG_ROUTINE.REQUEST, (state, action) => {
      return {
        ...state,
        ...action.payload,
      };
    })
    .addCase(DELETE_CONVERSATION_ROUTINE.SUCCESS, (state, action) => {
      const conversationIds = Array.isArray(action.payload)
        ? action.payload
        : [action.payload];
      const conversations: Conversations["conversations"] =
        state.conversations || [];

      return {
        ...state,
        conversations: conversations.filter((conversation) => {
          return !conversationIds.includes(conversation.id);
        }),
      };
    })
    .addCase(GET_CONVERSATIONS_COUNTER_ROUTINE.REQUEST, (state) => {
      return {
        ...state,
        isConversationCountersLoading: true,
      };
    })
    .addCase(GET_CONVERSATIONS_COUNTER_ROUTINE.SUCCESS, (state, action) => {
      return {
        ...state,
        counter: action.payload,
        isConversationCountersLoading: false,
      };
    })
    .addCase(GET_CONVERSATIONS_COUNTER_ROUTINE.FAILURE, (state) => {
      return {
        ...state,
        isConversationCountersLoading: false,
      };
    })
    .addCase(ADD_CONVERSATION_ROUTINE.REQUEST, (state, action) => {
      const { conversationIdIndex, conversation } = action?.payload ?? {};
      const conversations: Conversations["conversations"] =
        state.conversations || [];

      return {
        ...state,
        conversations: immutableAdd(
          conversations,
          conversationIdIndex,
          conversation
        ),
      };
    })
    .addCase(REMOVE_CONVERSATION_ROUTINE.REQUEST, (state, action) => {
      const conversationIds = action.payload;
      const conversations: Conversations["conversations"] =
        state.conversations || [];
      return {
        ...state,
        conversations: conversations.map((conversation) => {
          if (conversationIds.includes(conversation.id)) {
            return {
              ...conversation,
              isComplete: true,
            };
          }
          return {
            ...conversation,
          };
        }),
      };
    })
    .addCase(REMOVE_CONVERSATION_ROUTINE.SUCCESS, (state, action) => {
      const conversationIds = action.payload;
      const conversations: Conversations["conversations"] =
        state.conversations || [];
      const newConversations = conversations.filter(
        (conversation) => !conversationIds.includes(conversation.id)
      );
      return {
        ...state,
        isConversationJustCleared: newConversations.length === 0,
        conversations: newConversations,
      };
    })
    .addCase(
      ADD_MESSAGE_ROUTINE.SUCCESS,
      (state: ConversationInboxState, action: addMessageSuccessAction) => {
        const { conversationId, message, ignoreIncreaseUnreadCount } =
          action.payload;
        const conversations = state.conversations || [];
        const convIdx = conversations.findIndex((c) => c.id === conversationId);

        const conv = conversations[convIdx];
        if (!conv || convIdx === -1) {
          return state;
        }

        const existingMessages = conv.messages ?? [];
        if (existingMessages.some((c) => c.id === message.id)) {
          return state;
        }

        const isMessagefromSked = message.sender?.platform === "SK";
        const unreadCountIncrease =
          ignoreIncreaseUnreadCount || isMessagefromSked ? 0 : 1;
        const unreadCount =
          conv.status === Status.closed
            ? 0
            : (conv.unreadCount ?? 0) + unreadCountIncrease;

        return {
          ...state,
          conversations: immutableUpdate(conversations, convIdx, {
            unreadCount,
            updatedAt: new Date().toISOString(),
            messages: orderMessages([...existingMessages, message]),
          }),
        };
      }
    )
    .addCase(
      REMOVE_MESSAGE_ROUTINE.SUCCESS,
      (state, action): ConversationInboxState => {
        const { conversationId, messageId } = action.payload;
        if (!messageId) {
          return state;
        }

        const conversations = state.conversations || [];
        const conversationIdIndex = conversations.findIndex(
          (c) => c.id === conversationId
        );

        if (conversationIdIndex === -1) {
          return state;
        }
        const conv = conversations[conversationIdIndex];
        if (!conv) {
          return state;
        }

        const messages = conv.messages ?? [];
        const messageIdIndex = messages.findIndex((m) => m.id === messageId);
        if (messageIdIndex === -1) {
          return state;
        }

        const newMessages = immutableUpdate(messages, messageIdIndex);
        const unreadCount = conversations[conversationIdIndex].unreadCount;
        const decrementUnreadCount =
          unreadCount > 0 && newMessages.length - messageIdIndex <= unreadCount;

        return {
          ...state,
          conversations: immutableUpdate(conversations, conversationIdIndex, {
            messages: newMessages,
            updatedAt:
              newMessages.at(-1)?.createdAt || new Date().toISOString(),
            ...(decrementUnreadCount ? { unreadCount: unreadCount - 1 } : {}),
          }),
        };
      }
    )
    .addCase(
      ADD_MESSAGE_REACTION_ROUTINE.SUCCESS,
      (
        state,
        { payload }: { payload: AddReactionPayload }
      ): ConversationInboxState => {
        return immutableMessageUpdate({
          state,
          convId: payload.conversationId,
          msgId: payload.messageId,
          updater: (foundMsg) => {
            if (foundMsg.type === MessageType.ttThreadedComment) {
              return {
                ...foundMsg,
                liked: true,
              };
            }
            let newReactions = (foundMsg.reactions || []).filter(
              (c) =>
                c.id !== payload.id &&
                c.id !== payload.tempId &&
                c.reactee !== payload.reactee
            );

            newReactions = [
              ...newReactions,
              {
                id: payload.id,
                reactee: payload.reactee,
                reaction: payload.reaction,
                emoji: payload.emoji,
                createdAt: new Date().toISOString(),
              },
            ];
            return {
              ...foundMsg,
              reactions: newReactions,
            };
          },
        });
      }
    )
    .addCase(
      DELETE_MESSAGE_STATUS_ROUTINE.SUCCESS,
      (state, action): ConversationInboxState => {
        const { conversationId, messageId } = action.payload;
        if (!messageId) {
          return state;
        }

        const conversations = state.conversations || [];
        const conversationIdIndex = conversations.findIndex(
          (c) => c.id === conversationId
        );

        if (conversationIdIndex === -1) {
          return state;
        }
        const conv = conversations[conversationIdIndex];
        if (!conv) {
          return state;
        }

        const messages = conv.messages ?? [];
        const messageIdIndex = messages.findIndex((m) => m.id === messageId);
        if (messageIdIndex === -1) {
          return state;
        }

        const targetMessage = messages[messageIdIndex];
        if (!targetMessage) {
          return state;
        }

        const newMessages = immutableUpdate(messages, messageIdIndex, {
          ...targetMessage,
          deleted: true,
        });

        return {
          ...state,
          conversations: immutableUpdate(conversations, conversationIdIndex, {
            messages: newMessages,
            updatedAt: new Date().toISOString(),
          }),
        };
      }
    )
    .addCase(
      REMOVE_MESSAGE_REACTION_ROUTINE.SUCCESS,
      (
        state,
        { payload }: { payload: RemoveReactionPayload }
      ): ConversationInboxState => {
        return immutableMessageUpdate({
          state,
          convId: payload.conversationId,
          msgId: payload.messageId,
          updater: (foundMsg) => {
            if (foundMsg.type === MessageType.ttThreadedComment) {
              return {
                ...foundMsg,
                liked: false,
              };
            }

            const newReactions = (foundMsg.reactions || []).filter(
              (c) => c.id !== payload.id && c.reactee !== payload.reactee
            );
            return {
              ...foundMsg,
              reactions: newReactions,
            };
          },
        });
      }
    )
    .addCase(
      setMessageReactionProcessing,
      (state, action): ConversationInboxState => {
        const payload = action.payload as setMessageReactionProcessingPayload;
        return immutableMessageUpdate({
          state,
          convId: payload.conversationId,
          msgId: payload.messageId,
          updater: (foundMsg) => {
            return {
              ...foundMsg,
              isReationProcessing: payload.isProcessing,
            };
          },
        });
      }
    )
    .addCase(
      ADD_COLLABORATOR_TYPING_ROUTINE.SUCCESS,
      (state, action: collaboratorTypingAction) => {
        const { typingCollaborators = [] } = state;
        const { payload } = action;
        if (payload.enabled) {
          return {
            ...state,
            typingCollaborators: immutableUpdateByIdOrAdd(
              typingCollaborators,
              payload.id,
              {
                ...payload,
                createdAt: Date.now(),
              }
            ),
          };
        }
        return {
          ...state,
          typingCollaborators: immutableRemoveById(
            typingCollaborators,
            payload.id
          ),
        };
      }
    )
    .addCase(REMOVE_COLLABORATOR_TYPING_ROUTINE.SUCCESS, (state, action) => {
      return {
        ...state,
        typingCollaborators: immutableRemoveById(
          state.typingCollaborators || [],
          action.payload.id
        ),
      };
    })
    .addCase(
      SET_COLLABORATOR_TYPINGS_ROUTINE.SUCCESS,
      (state, { payload }: { payload: TypingCollaborator[] }) => {
        return {
          ...state,
          typingCollaborators: payload,
        };
      }
    )
    .addCase(RESYNC_INTERLOCUTOR.SUCCESS, (state, action) => {
      const conversations = state.conversations || [];
      const conversationToUpdateIndex = conversations?.findIndex(
        (conv) => conv.id === action.payload.conversationId
      );
      const conversationToUpdate = conversations[conversationToUpdateIndex];

      if (!conversationToUpdate) {
        return state;
      }

      const updatedConversation = {
        ...conversationToUpdate,
        interlocutor: {
          ...conversationToUpdate.interlocutor,
          image: action.payload.image,
        },
      };

      return {
        ...state,
        conversations: immutableUpdate(
          conversations,
          conversationToUpdateIndex,
          updatedConversation
        ),
      };
    })
    .addCase(CLOSE_ALL_CONVERSATIONS_ROUTINE.REQUEST, (state) => {
      return {
        ...state,
        isClosingAllConversationRequest: true,
      };
    })
    .addCase(CLOSE_ALL_CONVERSATIONS_ROUTINE.SUCCESS, (state) => {
      return {
        ...state,
        isClosingAllConversationRequest: false,
      };
    })
    .addCase(CLOSE_ALL_CONVERSATIONS_ROUTINE.FAILURE, (state) => {
      return {
        ...state,
        isClosingAllConversationRequest: false,
      };
    })
    .addCase(
      SET_CONVERSATION_TYPES_FILTER_ROUTINE.REQUEST,
      (
        state,
        action: setConversationTypesFilterAction
      ): ConversationInboxState => {
        const normalizedTypes = normalizeInboxConversationTypes(
          action.payload.types
        );
        localStorage.setItem(
          InboxLocalStorageKey.inboxSelectedConversationTypes,
          JSON.stringify(normalizedTypes)
        );
        return {
          ...state,
          types: normalizedTypes,
        };
      }
    )
    .addCase(SELECT_CONVERSATIONS_ROUTINE.REQUEST, (state, action) => {
      const { selected, conversationIds }: selectConversationsPayload =
        action.payload;

      if (conversationIds.length === 0) {
        return {
          ...state,
          selectedConversations: [],
        };
      }

      const selectedConversations = selected
        ? [...new Set([...state.selectedConversations, ...conversationIds])]
        : state.selectedConversations.filter(
            (convId) => !conversationIds.includes(convId)
          );

      return {
        ...state,
        selectedConversations,
      };
    })
    .addCase(
      INBOX_SET_MESSAGE_STATUS_REMOVED_ROUTINE.REQUEST,
      (
        state,
        { payload: { conversationId, id } }: setMessageStatusRemovedAction
      ): ConversationInboxState => {
        return immutableMessageUpdate({
          state,
          convId: conversationId,
          msgId: id,
          updater: (msg) => ({ ...msg, deleted: true }),
        });
      }
    )
    .addCase(
      INBOX_SET_MESSAGE_STATUS_REMOVED_ROUTINE.FAILURE,
      (
        state,
        { payload: { conversationId, id } }: setMessageStatusRemovedAction
      ): ConversationInboxState => {
        return immutableMessageUpdate({
          state,
          convId: conversationId,
          msgId: id,
          updater: (msg) => ({ ...msg, deleted: false }),
        });
      }
    )
    .addCase(
      INBOX_SET_MESSAGE_STATUS_EDITING_ROUTINE.REQUEST,
      (
        state,
        { payload: { message, action } }: setMessageStatusEditingAction
      ): ConversationInboxState => {
        return {
          ...state,
          editingMessage: action === "enable" && message ? message : null,
        };
      }
    )
    .addCase(
      INBOX_SET_MESSAGE_STATUS_EDITING_ROUTINE.FAILURE,
      (state): ConversationInboxState => {
        return {
          ...state,
          editingMessage: null,
        };
      }
    )
    .addCase(
      INBOX_SET_MESSAGE_HIDDEN_ROUTINE.REQUEST,
      (
        state,
        { payload: { conversationId, id, hidden } }: setMessageHiddenAction
      ): ConversationInboxState => {
        return immutableMessageUpdate({
          state,
          convId: conversationId,
          msgId: id,
          updater: (msg) => ({ ...msg, hidden, isHiddenProcessing: true }),
        });
      }
    )
    .addCase(
      INBOX_SET_MESSAGE_HIDDEN_ROUTINE.SUCCESS,
      (
        state,
        { payload: { conversationId, id, hidden } }: setMessageHiddenAction
      ): ConversationInboxState => {
        return immutableMessageUpdate({
          state,
          convId: conversationId,
          msgId: id,
          updater: (msg) => ({ ...msg, hidden, isHiddenProcessing: false }),
        });
      }
    )
    .addCase(
      INBOX_SET_MESSAGE_HIDDEN_ROUTINE.FAILURE,
      (
        state,
        { payload: { conversationId, id, hidden } }: setMessageHiddenAction
      ): ConversationInboxState => {
        return immutableMessageUpdate({
          state,
          convId: conversationId,
          msgId: id,
          updater: (msg) => ({
            ...msg,
            hidden: !hidden,
            isHiddenProcessing: false,
          }),
        });
      }
    )
    .addCase(
      RESYNC_POST_CONTENT.SUCCESS,
      (
        state: ConversationInboxState,
        { payload: post }: { payload: ConversationPost }
      ): ConversationInboxState => {
        const excludeFields: (keyof ConversationPost)[] = ["id", "owner"];
        return {
          ...state,
          conversations: updateConversationPosts(state.conversations, post.id, {
            ...omit(post, excludeFields),
            resyncing: false,
            resyncRequested: true,
          }),
        };
      }
    )
    .addCase(
      RESYNC_POST_CONTENT.REQUEST,
      (
        state: ConversationInboxState,
        { payload }: { payload: { postId: string } }
      ): ConversationInboxState => {
        return {
          ...state,
          conversations: updateConversationPosts(
            state.conversations,
            payload.postId,
            {
              resyncing: true,
              resyncRequested: true,
            }
          ),
        };
      }
    )
    .addCase(
      RESYNC_POST_CONTENT.FAILURE,
      (
        state: ConversationInboxState,
        { payload }: { payload: { postId: string } }
      ): ConversationInboxState => {
        return {
          ...state,
          conversations: updateConversationPosts(
            state.conversations,
            payload.postId,
            {
              resyncing: false,
              resyncRequested: true,
            }
          ),
        };
      }
    )
    .addCase(
      setSearchTerm,
      (
        state: ConversationInboxState,
        action: { payload: string }
      ): ConversationInboxState => {
        return {
          ...state,
          searchTerm: action.payload,
        };
      }
    )
    .addCase(
      setSentimentFilter,
      (
        state: ConversationInboxState,
        action: { payload: string[] }
      ): ConversationInboxState => {
        return {
          ...state,
          sentiment: action.payload,
        };
      }
    )
    .addCase(
      setVisibleFacets,
      (
        state: ConversationInboxState,
        action: { payload: string[] }
      ): ConversationInboxState => {
        return {
          ...state,
          visibleFacets: action.payload,
        };
      }
    )
    .addCase(
      hydrateInboxFiltersFromUrl,
      (
        state: ConversationInboxState,
        action: { payload: HydrateInboxFiltersFromUrlPayload }
      ): ConversationInboxState => {
        const {
          tags,
          types,
          searchTerm,
          sentiment,
          dateFrom,
          dateTo,
          ...config
        } = action.payload;

        return {
          ...state,
          ...config,
          types: normalizeInboxConversationTypes(types),
          searchTerm,
          sentiment,
          tagFilter: tags,
          dateFrom,
          dateTo,
        };
      }
    )
    .addCase(
      setTagFilter,
      (
        state: ConversationInboxState,
        action: { payload: string[] }
      ): ConversationInboxState => {
        return {
          ...state,
          tagFilter: action.payload,
        };
      }
    )
    .addCase(
      setDateRangeFilter,
      (
        state: ConversationInboxState,
        action: { payload: { dateFrom: string | null; dateTo: string | null } }
      ): ConversationInboxState => {
        return {
          ...state,
          dateFrom: action.payload.dateFrom,
          dateTo: action.payload.dateTo,
        };
      }
    )
    .addCase(
      fetchWorkspaceTags,
      (state: ConversationInboxState): ConversationInboxState => {
        return {
          ...state,
          isWorkspaceTagsLoading: true,
          workspaceTagsError: null,
        };
      }
    )
    .addCase(
      fetchWorkspaceTagsSuccess,
      (
        state: ConversationInboxState,
        action: { payload: Tag[] }
      ): ConversationInboxState => {
        return {
          ...state,
          workspaceTags: action.payload,
          isWorkspaceTagsLoading: false,
          workspaceTagsError: null,
        };
      }
    )
    .addCase(
      fetchWorkspaceTagsFailure,
      (
        state: ConversationInboxState,
        action: { payload?: string }
      ): ConversationInboxState => {
        return {
          ...state,
          isWorkspaceTagsLoading: false,
          workspaceTagsError: action.payload || "Unable to load tags",
        };
      }
    )
    .addCase(
      createWorkspaceTagSuccess,
      (
        state: ConversationInboxState,
        action: { payload: Tag }
      ): ConversationInboxState => {
        return {
          ...state,
          workspaceTags: [...state.workspaceTags, action.payload],
        };
      }
    )
    .addCase(
      createWorkspaceTagFailure,
      (
        state: ConversationInboxState,
        action: { payload?: string }
      ): ConversationInboxState => {
        return {
          ...state,
          workspaceTagsError: action.payload || "Unable to create tag",
        };
      }
    )
    .addCase(
      updateWorkspaceTagSuccess,
      (
        state: ConversationInboxState,
        action: { payload: Tag }
      ): ConversationInboxState => {
        return {
          ...state,
          workspaceTags: state.workspaceTags.map((tag) =>
            tag.id === action.payload.id ? action.payload : tag
          ),
        };
      }
    )
    .addCase(
      updateWorkspaceTagFailure,
      (state: ConversationInboxState): ConversationInboxState => {
        return state;
      }
    )
    .addCase(
      deleteWorkspaceTagSuccess,
      (
        state: ConversationInboxState,
        action: { payload: { id: string } }
      ): ConversationInboxState => {
        const deletedId = action.payload.id;
        const deletedTag = state.workspaceTags.find((t) => t.id === deletedId);
        const existingArchived = state.archivedTags ?? [];
        const alreadyArchived = existingArchived.some(
          (t) => t.id === deletedId
        );
        return {
          ...state,
          archivedTags:
            deletedTag && !alreadyArchived
              ? [...existingArchived, { ...deletedTag, archived: true }]
              : existingArchived,
          workspaceTags: state.workspaceTags.filter(
            (tag) => tag.id !== deletedId
          ),
          tagFilter: (state.tagFilter ?? []).filter((id) => id !== deletedId),
          conversations: state.conversations?.map((conv) =>
            conv.tags?.some((tag) => tag.id === deletedId)
              ? {
                  ...conv,
                  tags: conv.tags.filter((tag) => tag.id !== deletedId),
                }
              : conv
          ),
        };
      }
    )
    .addCase(
      deleteWorkspaceTagFailure,
      (state: ConversationInboxState): ConversationInboxState => {
        return state;
      }
    )
    .addCase(
      fetchArchivedTags,
      (state: ConversationInboxState): ConversationInboxState => {
        return {
          ...state,
          isArchivedTagsLoading: true,
          archivedTagsError: null,
        };
      }
    )
    .addCase(
      fetchArchivedTagsSuccess,
      (
        state: ConversationInboxState,
        action: { payload: Tag[] }
      ): ConversationInboxState => {
        return {
          ...state,
          archivedTags: action.payload,
          isArchivedTagsLoading: false,
          archivedTagsError: null,
        };
      }
    )
    .addCase(
      fetchArchivedTagsFailure,
      (
        state: ConversationInboxState,
        action: { payload?: string }
      ): ConversationInboxState => {
        return {
          ...state,
          isArchivedTagsLoading: false,
          archivedTagsError: action.payload || "Unable to load archived tags",
        };
      }
    )
    .addCase(
      restoreWorkspaceTag,
      (
        state: ConversationInboxState,
        action: { payload: { id: string } }
      ): ConversationInboxState => {
        const { id } = action.payload;
        if (state.restoringTagIds.includes(id)) return state;
        return {
          ...state,
          restoringTagIds: [...state.restoringTagIds, id],
        };
      }
    )
    .addCase(
      restoreWorkspaceTagSuccess,
      (
        state: ConversationInboxState,
        action: { payload: Tag }
      ): ConversationInboxState => {
        const restoredTag = action.payload;
        // Server returns workspaceTags in createdAt-ascending order (new
        // tags are appended by createWorkspaceTagSuccess), so place the
        // restored tag at its chronological position rather than at the
        // end — avoids an unnecessary refetch while keeping the user's
        // list in a stable, expected order.
        const mergedWorkspaceTags = [
          ...state.workspaceTags.filter((t) => t.id !== restoredTag.id),
          restoredTag,
        ].sort((a, b) => a.createdAt.localeCompare(b.createdAt));
        return {
          ...state,
          restoringTagIds: state.restoringTagIds.filter(
            (id) => id !== restoredTag.id
          ),
          archivedTags: (state.archivedTags ?? []).filter(
            (t) => t.id !== restoredTag.id
          ),
          workspaceTags: mergedWorkspaceTags,
        };
      }
    )
    .addCase(
      restoreWorkspaceTagFailure,
      (
        state: ConversationInboxState,
        action: { payload: { id: string } }
      ): ConversationInboxState => {
        return {
          ...state,
          restoringTagIds: state.restoringTagIds.filter(
            (id) => id !== action.payload.id
          ),
        };
      }
    )
    .addCase(
      bulkAddConversationTagSuccess,
      (
        state: ConversationInboxState,
        action: { payload: { conversationIds: string[]; tag: Tag } }
      ): ConversationInboxState => {
        const { conversationIds, tag } = action.payload;
        const idSet = new Set(conversationIds);
        return {
          ...state,
          conversations: state.conversations?.map((conv) => {
            if (!idSet.has(conv.id)) return conv;
            const alreadyHasTag = conv.tags?.some((t) => t.id === tag.id);
            if (alreadyHasTag) return conv;
            return { ...conv, tags: [...(conv.tags ?? []), tag] };
          }),
        };
      }
    )
    .addCase(
      bulkAddConversationTagFailure,
      (state: ConversationInboxState): ConversationInboxState => {
        return state;
      }
    )
    .addCase(
      bulkRemoveConversationTagSuccess,
      (
        state: ConversationInboxState,
        action: { payload: { conversationIds: string[]; tagId: string } }
      ): ConversationInboxState => {
        const { conversationIds, tagId } = action.payload;
        const idSet = new Set(conversationIds);
        return {
          ...state,
          conversations: state.conversations?.map((conv) => {
            if (!idSet.has(conv.id)) return conv;
            return {
              ...conv,
              tags: (conv.tags ?? []).filter((t) => t.id !== tagId),
            };
          }),
        };
      }
    )
    .addCase(
      bulkRemoveConversationTagFailure,
      (state: ConversationInboxState): ConversationInboxState => {
        return state;
      }
    );
});

function updateConversationPosts(
  conversations: Conversation[] | undefined,
  postId: string,
  data: Partial<ConversationPost>
): Conversation[] {
  return (conversations || []).map((conv) => {
    if (conv.post?.id === postId) {
      return {
        ...conv,
        post: {
          ...conv.post,
          ...data,
        },
      };
    }
    return conv;
  });
}

export function orderMessages<T extends Message>(messages: T[]): T[] {
  messages = uniqBy(messages, (c) => c.id);
  return orderBy(
    messages,
    [
      (c) => {
        return getMessageCreatedAtAsTime(c);
      },
      (c) => (c.type === MessageType.system ? 1 : 0),
    ],
    ["asc", "asc"]
  );
}
export const getMessageCreatedAtAsTime = (message: Message): number => {
  try {
    if (typeof message.createdAt === "string") {
      return new Date(message.createdAt).getTime();
    }
    if (message.createdAt instanceof Date) {
      return message.createdAt.getTime();
    }
    return message.createdAt;
  } catch {
    return Date.now();
  }
};

export type RootState = ReturnType<typeof conversationInboxReducer>;

export default conversationInboxReducer;
